import React from "react";
import { Link } from "react-router-dom";
import PageShell, { SectionHeader } from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import { CompassMark } from "@/components/CompassMark";
import { COACHING, PORTRAITS, getObservation } from "@/data/content";

const COACH_INTENT = "/contact?intent=coaching";

function LaneCard({ lane }) {
  const obs = lane.relatedObs ? getObservation(lane.relatedObs) : null;
  return (
    <li
      id={lane.slug}
      data-testid={`coaching-lane-${lane.slug}`}
      className="bg-ink-base p-8 md:p-10 flex flex-col scroll-mt-28"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">{lane.number}</p>
      <h3 className="mt-4 font-serif text-2xl md:text-3xl text-paper-primary leading-tight">{lane.name}</h3>
      <p className="mt-4 font-serif text-lg text-gold-warm/90 leading-snug text-pretty">{lane.summary}</p>
      <p className="mt-5 text-paper-secondary text-base leading-relaxed text-pretty">{lane.body}</p>
      {obs && (
        <Link
          to={`/thinking/${obs.slug}`}
          data-testid={`coaching-lane-related-${lane.slug}`}
          className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gold/70 hover:text-gold-warm transition-colors duration-300"
        >
          <span>Related thinking: {obs.headline}</span>
          <span>→</span>
        </Link>
      )}
    </li>
  );
}

export default function Coaching() {
  return (
    <PageShell
      testId="page-coaching"
      title="Coaching | Quentin Young"
      description="Personal, business, marketing, and sales coaching with Quentin Young for people seeking sharper judgment, stronger positioning, better execution, and clearer self-command."
      ogImage="/og-image.jpg"
      breadcrumbs={[{ name: "Home", url: "/" }, { name: "Coaching", url: "/coaching" }]}
      jsonLd={[
        {
          id: "ld-coaching-service",
          data: {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Coaching",
            name: "Coaching with Quentin Young",
            provider: { "@type": "Person", name: "Quentin Young", url: "https://quentinyoung.ca" },
            areaServed: "Worldwide",
            description:
              "Personal, business, marketing, and sales coaching focused on judgment, positioning, execution, and self-command.",
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Coaching lanes",
              itemListElement: COACHING.lanes.map((l) => ({
                "@type": "OfferCatalog",
                name: l.name,
                description: l.summary,
              })),
            },
          },
        },
      ]}
    >
      {/* Hero */}
      <section className="container-edge pt-16 md:pt-24 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <p className="eyebrow-gold mb-6" data-testid="coaching-eyebrow">{COACHING.eyebrow}</p>
            <h1 className="display-serif text-4xl md:text-5xl lg:text-6xl text-paper-primary text-balance leading-[1.02]">
              {COACHING.h1}
            </h1>
            <div className="mt-10 space-y-1.5">
              {COACHING.opening.map((line, i) => (
                <p key={i} className="font-serif text-xl md:text-2xl text-paper-secondary leading-snug">
                  {line}
                </p>
              ))}
            </div>
            <p className="mt-10 max-w-2xl font-serif text-lg md:text-xl text-paper-primary leading-relaxed text-pretty">
              {COACHING.lead}
            </p>
            <p className="mt-6 max-w-2xl text-paper-tertiary text-base leading-relaxed text-pretty">
              {COACHING.connective}
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-6">
              <Link to={COACH_INTENT} data-testid="coaching-hero-cta" className="btn-quiet">
                <span>{COACHING.ctaLabel}</span>
                <span>→</span>
              </Link>
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute max-w-xs">
                {COACHING.availability}
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative overflow-hidden border border-ink-line bg-ink-surface">
              <img
                src={PORTRAITS.seatedThinker}
                alt="Quentin Young"
                className="w-full h-full object-cover aspect-[4/5] grayscale-[0.15]"
                data-testid="coaching-portrait"
              />
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(14,14,14,0) 55%, rgba(14,14,14,0.6) 100%)" }} />
              <p className="absolute bottom-4 left-5 font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
                One to one. Direct.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lanes */}
      <section className="border-t border-ink-line bg-ink-base py-16 md:py-24" data-testid="coaching-lanes">
        <div className="container-edge">
          <SectionHeader eyebrow="Four lanes" title="Where the work concentrates." />
          <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-line border border-ink-line">
            {COACHING.lanes.map((lane) => (
              <LaneCard key={lane.slug} lane={lane} />
            ))}
          </ul>
        </div>
      </section>

      {/* How sessions work */}
      <section className="border-t border-ink-line bg-ink-base py-16 md:py-24">
        <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <SectionHeader eyebrow="How sessions work" title="Built around the problem." />
          </div>
          <ul className="lg:col-span-8 space-y-6" data-testid="coaching-how">
            {COACHING.howItWorks.map((item, i) => (
              <li key={i} className="flex gap-5">
                <span className="font-mono text-[11px] text-gold pt-1.5">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-paper-secondary text-lg leading-relaxed text-pretty">{item}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Who it is / is not for */}
      <section className="border-t border-ink-line bg-ink-base py-16 md:py-24">
        <div className="container-edge grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-px md:bg-ink-line md:border md:border-ink-line">
          <div className="md:bg-ink-base md:p-10 lg:p-12" data-testid="coaching-who-for">
            <p className="eyebrow-gold mb-6">Who it is for</p>
            <ul className="space-y-4 text-paper-secondary text-base leading-relaxed">
              {COACHING.whoFor.map((w, i) => (
                <li key={i} className="flex gap-3"><span className="text-gold">·</span><span>{w}</span></li>
              ))}
            </ul>
          </div>
          <div className="md:bg-ink-base md:p-10 lg:p-12" data-testid="coaching-who-not">
            <p className="eyebrow mb-6">Who it is not for</p>
            <ul className="space-y-4 text-paper-tertiary text-sm leading-relaxed">
              {COACHING.whoNotFor.map((w, i) => (
                <li key={i} className="flex gap-3"><span className="text-paper-mute">·</span><span>{w}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Start the conversation */}
      <section className="border-t border-ink-line bg-ink-surface/40 py-20 md:py-28">
        <div className="container-edge flex flex-col md:flex-row md:items-center md:justify-between gap-10">
          <div className="max-w-2xl">
            <CompassMark size={44} opacity={0.4} className="mb-8" alt="" />
            <h2 className="display-serif text-3xl md:text-5xl text-paper-primary text-balance leading-[1.05]">
              Bring the situation forward.
            </h2>
            <p className="mt-6 text-paper-secondary text-lg leading-relaxed max-w-xl text-pretty">
              Describe what you are building, leading, selling, or becoming. The first conversation is about fit and the real problem.
            </p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
              {COACHING.availability}
            </p>
          </div>
          <Link to={COACH_INTENT} data-testid="coaching-cta" className="btn-quiet shrink-0">
            <span>{COACHING.ctaLabel}</span>
            <span>→</span>
          </Link>
        </div>
      </section>

      <NextRoom
        eyebrow="Move to the next room"
        title="Understand how Quentin sees it."
        rooms={[
          { label: "Explore the thinking", to: "/thinking", hint: "underlying ideas" },
          { label: "See the work in practice", to: "/building", hint: "the work" },
          { label: "Read the philosophy", to: "/philosophy", hint: "operating logic" },
        ]}
        testId="coaching-next"
      />
    </PageShell>
  );
}
